/**
 * Analytics (E14-T06): the tenant's calls, latency and cost over a week, a month or a quarter
 * (GET /v1/analytics). The chart is drawn here (a few bars need no chart library), and every chart
 * has the same numbers as a table.
 */
import { useMutation } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { useId, useState } from "react";
import { data, type Schemas } from "../api/client";
import { Page, useApiQuery, useDenied } from "../app/Page";
import { useBff } from "../app/session";
import { save } from "../compliance/files";
import { Button } from "../ui/Button";
import { useFormat, useT, type MessageId } from "../i18n";
import { Empty, ErrorState, QueryView } from "../ui/states";
import { Reports } from "./Reports";
import { Table } from "../ui/Table";

type Analytics = Schemas["AnalyticsOut"];
type Outcome = keyof Analytics["outcomes"];
const OUTCOMES: Outcome[] = ["completed", "failed", "busy", "no_answer", "blocked"];
const COLORS: Record<Outcome, string> = {
  completed: "var(--color-primary)", failed: "var(--color-danger)", busy: "#d97706", no_answer: "#64748b",
  blocked: "#7c3aed",
};
const PERIODS = [7, 30, 90] as const;

/** YYYY-MM-DD of a day `back` days before today (the browser's day; the API answers in the tenant's). */
function daysAgo(back: number): string {
  const d = new Date();
  d.setDate(d.getDate() - back);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] p-4">
      <dt className="text-sm text-[var(--color-muted)]">{label}</dt>
      <dd className="mt-1 text-2xl font-semibold tabular-nums">{value}</dd>
      {hint && <dd className="text-xs text-[var(--color-muted)]">{hint}</dd>}
    </div>
  );
}

/** Calls a day, stacked by outcome: an SVG for the eye, described for a screen reader. */
function CallsChart({ a }: { a: Analytics }) {
  const t = useT();
  const max = Math.max(1, ...a.days.map((d) => d.calls));
  const width = 100 / a.days.length;
  return (
    <figure className="space-y-2">
      <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="h-48 w-full"
        role="img" aria-label={t("analytics.chart.label", { calls: a.calls, days: a.days.length })}>
        {a.days.map((d, i) => {
          let y = 40;
          return OUTCOMES.map((o) => {
            const h = (d.outcomes[o] / max) * 38;
            y -= h;
            return h > 0 ? (
              <rect key={`${d.date}-${o}`} x={i * width + width * 0.15} y={y} width={width * 0.7} height={h}
                fill={COLORS[o]}><title>{`${d.date}: ${d.outcomes[o]} ${t(`analytics.outcome.${o}` as MessageId)}`}</title></rect>
            ) : null;
          });
        })}
      </svg>
      <figcaption className="flex flex-wrap gap-4 text-xs text-[var(--color-muted)]">
        {OUTCOMES.map((o) => (
          <span key={o} className="flex items-center gap-1">
            <span aria-hidden="true" className="inline-block h-2 w-2 rounded-sm" style={{ background: COLORS[o] }} />
            {t(`analytics.outcome.${o}` as MessageId)}
          </span>
        ))}
      </figcaption>
    </figure>
  );
}

function Report({ a }: { a: Analytics }) {
  const t = useT();
  const format = useFormat();
  const pct = (n: number) => (a.calls ? `${Math.round((n / a.calls) * 100)}%` : "–");
  const seconds = (s: number | undefined) => (s === undefined ? "–" : t("analytics.seconds", { s: s.toFixed(1) }));
  if (a.calls === 0) {
    return <Empty title={t("analytics.empty.title")}><p>{t("analytics.empty.body")}</p></Empty>;
  }
  const costPerCall = a.cost_usd != null && a.calls ? a.cost_usd / a.calls : null;
  return (
    <div className="space-y-8">
      <dl className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat label={t("analytics.calls")} value={format.number(a.calls)}
          hint={t("analytics.minutes", { minutes: format.number(Math.round(a.minutes)) })} />
        <Stat label={t("analytics.failed")} value={pct(a.outcomes.failed)}
          hint={t("analytics.failedHint", { n: a.outcomes.failed })} />
        <Stat label={t("analytics.setup")} value={seconds(a.setup_seconds?.p95)}
          hint={a.setup_seconds ? t("analytics.p50", { value: seconds(a.setup_seconds.p50) }) : undefined} />
        <Stat label={t("analytics.turn")} value={seconds(a.turn_latency_ms ? a.turn_latency_ms.p95 / 1000 : undefined)}
          hint={a.turn_latency_ms ? t("analytics.p50", { value: seconds(a.turn_latency_ms.p50 / 1000) }) : undefined} />
        {a.cost_usd != null && (
          <Stat label={t("analytics.cost")} value={format.money(a.cost_usd, "USD")}
            hint={costPerCall != null ? t("analytics.perCall", { cost: format.money(costPerCall, "USD") }) : undefined} />
        )}
      </dl>

      <section aria-labelledby="calls-a-day" className="space-y-3">
        <h2 id="calls-a-day" className="text-lg font-semibold">{t("analytics.callsADay")}</h2>
        <CallsChart a={a} />
        <details>
          <summary className="cursor-pointer text-sm text-[var(--color-primary)]">{t("analytics.asTable")}</summary>
          <div className="mt-2">
            <Table caption={t("analytics.callsADay")} rowKey={(d) => d.date} rows={a.days} columns={[
              { header: t("analytics.day"), cell: (d) => format.date(d.date) },
              { header: t("analytics.calls"), cell: (d) => d.calls },
              ...OUTCOMES.map((o) => ({ header: t(`analytics.outcome.${o}` as MessageId), cell: (d: typeof a.days[number]) => d.outcomes[o] })),
              { header: t("analytics.minutesHeader"), cell: (d) => d.minutes },
            ]} />
          </div>
        </details>
      </section>

      {a.cost_by_component && Object.keys(a.cost_by_component).length > 0 && (
        <section aria-labelledby="cost" className="space-y-3">
          <h2 id="cost" className="text-lg font-semibold">{t("analytics.costByComponent")}</h2>
          <Table caption={t("analytics.costByComponent")} rowKey={([k]) => k}
            rows={Object.entries(a.cost_by_component)} columns={[
              { header: t("analytics.component"), cell: ([k]) => k.toUpperCase() },
              { header: t("analytics.cost"), cell: ([, v]) => format.money(v, "USD") },
            ]} />
        </section>
      )}

      <div className="grid gap-8 lg:grid-cols-2">
        <section aria-labelledby="failures" className="space-y-3">
          <h2 id="failures" className="text-lg font-semibold">{t("analytics.failures")}</h2>
          {a.failures.length === 0 ? <p className="text-sm text-[var(--color-muted)]">{t("analytics.noFailures")}</p> : (
            <Table caption={t("analytics.failures")} rows={a.failures}
              rowKey={(f) => `${f.status}-${f.reason}-${f.sip_code ?? ""}`} columns={[
                { header: t("analytics.reason"), cell: (f) => f.reason || f.status },
                { header: t("analytics.sip"), cell: (f) => f.sip_code ?? "–" },
                { header: t("analytics.calls"), cell: (f) => f.count },
              ]} />
          )}
        </section>
        <section aria-labelledby="providers" className="space-y-3">
          <h2 id="providers" className="text-lg font-semibold">{t("analytics.fallbacks")}</h2>
          {a.fallbacks.length === 0 ? <p className="text-sm text-[var(--color-muted)]">{t("analytics.noFallbacks")}</p> : (
            <Table caption={t("analytics.fallbacks")} rows={a.fallbacks}
              rowKey={(f) => `${f.kind}-${f.from_provider}-${f.to_provider}`} columns={[
                { header: t("analytics.kind"), cell: (f) => f.kind.toUpperCase() },
                { header: t("analytics.fromTo"), cell: (f) => `${f.from_provider} → ${f.to_provider}` },
                { header: t("analytics.times"), cell: (f) => f.count },
              ]} />
          )}
        </section>
        <section aria-labelledby="tools" className="space-y-3">
          <h2 id="tools" className="text-lg font-semibold">{t("analytics.tools")}</h2>
          {a.tools.length === 0 ? <p className="text-sm text-[var(--color-muted)]">{t("analytics.noTools")}</p> : (
            <Table caption={t("analytics.tools")} rows={a.tools} rowKey={(x) => x.tool} columns={[
              { header: t("analytics.tool"), cell: (x) => x.tool },
              { header: t("analytics.calls"), cell: (x) => x.calls },
              { header: t("analytics.errors"), cell: (x) => `${x.errors} (${Math.round((x.errors / Math.max(1, x.calls)) * 100)}%)` },
            ]} />
          )}
        </section>
      </div>
    </div>
  );
}

type Filter = { agent_id?: string; direction?: "inbound" | "outbound"; number?: string; campaign_id?: string };
const select = "rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm";

/** Which calls (V1-E10-T02): an agent, a direction, one of our numbers, a campaign. */
function Filters({ value, onChange }: { value: Filter; onChange: (f: Filter) => void }) {
  const t = useT();
  const id = useId();
  const agents = useApiQuery("agents", (api) => api.GET("/v1/agents", { params: { query: { limit: 100 } } }));
  const numbers = useApiQuery("phone-numbers", (api) => api.GET("/v1/phone-numbers", { params: { query: { limit: 100 } } }));
  const campaigns = useApiQuery("campaigns", (api) => api.GET("/v1/campaigns", { params: { query: { limit: 100 } } }));
  const set = (key: keyof Filter, v: string) => onChange({ ...value, [key]: v || undefined });
  const field = (key: keyof Filter, label: MessageId, options: { value: string; label: string }[]) => (
    <label htmlFor={`${id}-${key}`} className="flex flex-col gap-1 text-sm">
      {t(label)}
      <select id={`${id}-${key}`} className={select} value={value[key] ?? ""} onChange={(e) => set(key, e.target.value)}>
        <option value="">{t("analytics.f.any")}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
  return (
    <form aria-label={t("analytics.f.label")} className="flex flex-wrap items-end gap-3" onSubmit={(e) => e.preventDefault()}>
      {field("agent_id", "analytics.f.agent", (agents.data?.data ?? []).map((a) => ({ value: a.id, label: a.name })))}
      {field("direction", "analytics.f.direction", [
        { value: "inbound", label: t("analytics.f.inbound") }, { value: "outbound", label: t("analytics.f.outbound") }])}
      {field("number", "analytics.f.number", (numbers.data?.data ?? []).map((n) => ({ value: n.e164, label: n.e164 })))}
      {field("campaign_id", "analytics.f.campaign", (campaigns.data?.data ?? []).map((c) => ({ value: c.id, label: c.name })))}
    </form>
  );
}

export function AnalyticsPage() {
  const t = useT();
  const denied = useDenied();
  const [days, setDays] = useState<(typeof PERIODS)[number]>(7);
  const [filter, setFilter] = useState<Filter>({});
  const period = { from: daysAgo(days - 1), to: daysAgo(0) };
  const query = useApiQuery(`analytics-${days}-${JSON.stringify(filter)}`, (api) =>
    api.GET("/v1/analytics", { params: { query: { ...period, ...filter } } }));
  const { api } = useBff();
  const exported = useMutation({  // the period's calls, as the filters say (V1-E10-T04)
    mutationFn: async () => {
      const text = (await data(api.GET("/v1/calls/export", { params: { query: { ...period, ...filter, format: "csv" } },
                                                             parseAs: "text" }))) as unknown as string;
      save(new Blob([text], { type: "text/csv" }), `calls-${period.from}-${period.to}.csv`);
    },
  });
  return (
    <Page title={t("nav.analytics")} description={t("analytics.description")} actions={
      <div className="flex flex-wrap items-center gap-2">
      <Button variant="secondary" disabled={exported.isPending} onClick={() => exported.mutate()}>
        <Download size={16} aria-hidden="true" />
        {t("analytics.export")}
      </Button>
      <div role="radiogroup" aria-label={t("analytics.period")} className="flex gap-1">
        {PERIODS.map((n) => (
          <button key={n} type="button" role="radio" aria-checked={days === n} onClick={() => setDays(n)}
            className={`rounded-md px-3 py-1.5 text-sm ${days === n
              ? "bg-[var(--color-primary)] text-[var(--color-on-primary)]" : "border border-[var(--color-border)]"} focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]`}>
            {t("analytics.lastDays", { n })}
          </button>
        ))}
      </div>
      </div>
    }>
      <Filters value={filter} onChange={setFilter} />
      {exported.isError && <ErrorState error={exported.error} />}
      <QueryView query={query} denied={denied}>{(a) => <Report a={a} />}</QueryView>
      <Reports />
    </Page>
  );
}
